import { Input as HeroInput, InputProps as HeroInputProps } from '@heroui/react'
import { forwardRef } from 'react'

export interface InputProps extends Omit<HeroInputProps, 'classNames' | 'onChange'> {
  label?: string
  placeholder?: string
  classNameContainer?: string
  variant?: 'bordered' | 'flat' | 'faded' | 'underlined'
  classNames?: {
    label?: string[]
    input?: string[]
    innerWrapper?: string
    inputWrapper?: string[]
  }
  labelPlacement?: 'outside' | 'inside' | 'outside-left'
  name?: string
  isRequired?: boolean
  value?: string
  onChange?: (value: string) => void
  onBlur?: () => void
  errorMessage?: string
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      classNameContainer,
      variant = 'bordered',
      classNames,
      labelPlacement = 'outside',
      name,
      isRequired = false,
      value,
      onChange,
      onBlur,
      errorMessage,
      ...props
    },
    ref,
  ) => {
    return (
      <HeroInput
        ref={ref}
        fullWidth
        className="h-full"
        errorMessage={errorMessage}
        isRequired={isRequired}
        labelPlacement={labelPlacement}
        name={name}
        radius="sm"
        value={value}
        variant={variant}
        classNames={{
          label: [
            'text-gray-900',
            'dark:text-foreground',
            'group-data-[filled-within=true]:text-gray-900',
            'dark:group-data-[filled-within=true]:text-white',
            'group-data-[invalid=true]:text-danger',
            'dark:group-data-[invalid=true]:text-danger',
            ...(classNames?.label || []),
          ],
          input: [
            'bg-transparent',
            'text-gray-900',
            'dark:text-white',
            'placeholder:text-gray-500',
            'dark:placeholder:text-white/60',
            'group-data-[invalid=true]:placeholder:text-danger',
            ...(classNames?.input || []),
          ],
          innerWrapper: 'bg-transparent',
          inputWrapper: [
            'bg-gray-50',
            'dark:bg-[#1e1e20]',
            'border-gray-200',
            'dark:border-default-600',
            'hover:bg-gray-100',
            'dark:hover:bg-[#1e1e20]/70',
            'group-data-[focus=true]:bg-gray-100',
            'dark:group-data-[focus=true]:bg-[#1e1e20]/50',
            'group-data-[focus=true]:border-primary',
            'dark:group-data-[focus=true]:border-primary',
            '!cursor-text',
            ...(classNames?.inputWrapper || []),
          ],
        }}
        onBlur={onBlur}
        onValueChange={onChange}
        {...props}
      />
    )
  },
)

Input.displayName = 'Input'

export default Input
